/**
 * Single source of truth for "which admin surface do I use?".
 *
 * Authoring, review and audits each have exactly one canonical entry point.
 * Every tile, command-palette entry and cross-link should reference these
 * constants instead of hard-coding a legacy path.
 */

export const PATHWAY_STUDIO_PATH = "/app/admin/pathway-studio";
export const ADMIN_REVIEW_PATH = "/app/admin/review";
export const ADMIN_AUDITS_PATH = "/app/admin/audits";

export type AdminReviewTab = "stage" | "approvals" | "crosswalk" | "expert";
export type AdminAuditsTab = "daily" | "log" | "consent" | "alignment";

/**
 * Oversight surfaces are read-only queues over the one authoring pipeline, so
 * they open *inside* Pathway Studio with the gate they belong to preselected
 * rather than as separate destinations. `studioHref` builds those links.
 */
export type StudioPane = "review" | "curriculum" | "audits" | "testimonials";

export const STUDIO_PANE_STAGE: Record<StudioPane, string> = {
  review: "review",
  curriculum: "publish",
  audits: "publish",
  testimonials: "publish",
};

export function studioHref(opts: {
  stage?: string;
  pane?: StudioPane;
  pathway?: string;
  tab?: string;
  /** Pre-select a module inside Author / Review stage panes. */
  module?: string;
  /** Soft hint that Assist should be focused (Author stage only — not an oversight pane). */
  assist?: boolean;
  /** Open a specific cas1_assist_proposals row in Author Assist. */
  proposal?: string;
  /** Content Learning Loop event id (or `incident:<uuid>`). */
  cll?: string;
} = {}): string {
  const p = new URLSearchParams();
  const stage = opts.stage ?? (opts.pane ? STUDIO_PANE_STAGE[opts.pane] : undefined);
  if (opts.pathway) p.set("pathway", opts.pathway);
  if (stage) p.set("stage", stage);
  if (opts.pane) p.set("pane", opts.pane);
  else if (opts.assist) p.set("pane", "assist");
  if (opts.tab) p.set("tab", opts.tab);
  if (opts.module) p.set("module", opts.module);
  if (opts.proposal) p.set("proposal", opts.proposal);
  if (opts.cll) p.set("cll", opts.cll);
  const qs = p.toString();
  return qs ? `${PATHWAY_STUDIO_PATH}?${qs}` : PATHWAY_STUDIO_PATH;
}

/** Default tab is pathway approvals — module stage review already lives on the Review gate. */
export function adminReviewHref(tab: AdminReviewTab = "approvals"): string {
  return studioHref({ pane: "review", tab });
}

export function adminAuditsHref(tab: AdminAuditsTab = "daily"): string {
  return studioHref({ pane: "audits", tab });
}

/** Direct paths, for the standalone routes that still back these surfaces. */
export function adminReviewDirectHref(tab: AdminReviewTab = "stage"): string {
  return `${ADMIN_REVIEW_PATH}?tab=${tab}`;
}

export function adminAuditsDirectHref(tab: AdminAuditsTab = "daily"): string {
  return `${ADMIN_AUDITS_PATH}?tab=${tab}`;
}


export const ADMIN_REVIEW_TABS: { value: AdminReviewTab; label: string; hint: string }[] = [
  {
    value: "approvals",
    label: "Pathway approvals",
    hint: "Approve pathways, courses and modules once their modules have cleared stage review.",
  },
  {
    value: "crosswalk",
    label: "Crosswalk review",
    hint: "Check generated framework crosswalks before they count towards coverage.",
  },
  {
    value: "expert",
    label: "Expert verification",
    hint: "External reviewer links and responses across every pathway. Issue new links in Pathway Studio → Review.",
  },
  {
    value: "stage",
    label: "Module stage review",
    hint: "Per-module Decision (same tool as the Review gate). Prefer the gate for the pathway you have open.",
  },
];

export const ADMIN_AUDITS_TABS: { value: AdminAuditsTab; label: string; hint: string }[] = [
  {
    value: "daily",
    label: "Daily audit",
    hint: "Automated trust, safety and scalability runs with fix packs.",
  },
  {
    value: "log",
    label: "Audit log",
    hint: "Trail of sensitive actions taken by admins and the system.",
  },
  {
    value: "consent",
    label: "Consent audit",
    hint: "Consent acknowledgment records for regulated data views.",
  },
  {
    value: "alignment",
    label: "Alignment scans",
    hint: "Scheduled curriculum gap scans and their run history.",
  },
];

/**
 * The canonical explanation of which studio owns which step. Rendered by
 * `AuthoringWorkflowGuide` on the hub, both studios and the review page.
 */
export const AUTHORING_WORKFLOW_STEPS: {
  step: string;
  surface: string;
  href: string;
  use: string;
}[] = [
  {
    step: "Define → Align → Structure → Author → Review → Publish",
    surface: "Pathway Studio",
    href: PATHWAY_STUDIO_PATH,
    use: "The whole workflow for one pathway. Every stage tool — metadata, alignment, structure, content generation, stage review, publish readiness — opens inside the stage that owns it, already scoped to the pathway you picked.",
  },
  {
    step: "Cross-pathway review queues",
    surface: "Admin review (Studio pane)",
    href: adminReviewHref("approvals"),
    use: "Use when you are working through approvals across pathways: pathway approvals, crosswalks and expert verifier status (module Decision stays on the Review gate). Opens inside Pathway Studio.",
  },
  {
    step: "Fleet interventions",
    surface: "Exceptions",
    href: "/app/admin/exceptions",
    use: "Read-only inbox for encoder health, failed encodes, dual-LLM debt and preflight blockers. Each row deep-links into Pathway Studio; fix there, then leave.",
  },
  {
    step: "Fleet audits",
    surface: "Curriculum overview (Studio pane)",
    href: studioHref({ pane: "curriculum", tab: "readiness" }),
    use: "Read-only overviews across all pathways: readiness scorecards, content health, framework coverage, prerequisites and catalog preview. Opens inside Pathway Studio.",
  },
];

/** Map a legacy standalone admin path + optional tab onto a Studio pane href (P5.5). */
export function legacyAdminPathToStudio(pathname: string, search = ""): string | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const tab = params.get("tab") ?? undefined;
  if (pathname === ADMIN_REVIEW_PATH || pathname.endsWith("/admin/review")) {
    return adminReviewHref((tab as AdminReviewTab) || "approvals");
  }
  if (pathname === ADMIN_AUDITS_PATH || pathname.endsWith("/admin/audits")) {
    return adminAuditsHref((tab as AdminAuditsTab) || "daily");
  }
  if (pathname.includes("/admin/curriculum-console")) {
    return studioHref({ pane: "curriculum", tab: tab || "manage" });
  }
  return null;
}
